import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template59: Página de Shinjuku de Tokio
// Copia DIRECTA del componente Page3.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template59 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [shinjukuError, setShinjukuError] = useState(false);
	const [godzilaError, setGodzilaError] = useState(false);
	const [karaokeError, setKaraokeError] = useState(false);
	const [donQuijoteError, setDonQuijoteError] = useState(false);
	const [shinjukuStickerError, setShinjukuStickerError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const shinjukuImage = getImageSrc("shinjuku-main");
	const godzilaImage = getImageSrc("godzila");
	const karaokeImage = getImageSrc("karaoke");
	const donQuijoteImage = getImageSrc("don-quijote");
	const shinjukuSticker = getCanvasImageUrl("shinjuku-sticker");

	const introText = page.texts?.[1]?.content;
	const shinjukuTitle = page.texts?.[2]?.content;
	const shinjukuSubtitle = page.texts?.[3]?.content;
	const shinjukuText = page.texts?.[4]?.content;
	const parqueTitle = page.texts?.[5]?.content;
	const parqueText = page.texts?.[6]?.content;
	const sabiasQueText = page.texts?.[7]?.content;
	const miradorTitle = page.texts?.[8]?.content;
	const miradorTextShort = page.texts?.[9]?.content;
	const miradorTextLong = page.texts?.[10]?.content;
	const karaokeStickerHtml = page.texts?.[11]?.content;
	const kabukichoTitle = page.texts?.[12]?.content;
	const kabukichoText = page.texts?.[13]?.content;
	const sabiasQueKabukicho1Html = page.texts?.[14]?.content;
	const sabiasQueKabukicho2Html = page.texts?.[15]?.content;
	const sabiasQueKabukicho3Html = page.texts?.[16]?.content;
	const omoideTitle = page.texts?.[17]?.content;
	const omoideText = page.texts?.[18]?.content;
	const comoLlegarTitle = page.texts?.[19]?.content;
	const comoLlegarListHtml = page.texts?.[20]?.content;
	const stickerDonQuijoteHtml = page.texts?.[21]?.content;

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};
	const sabiasQueKabukicho1 = parseSabiasQueItem(sabiasQueKabukicho1Html);
	const sabiasQueKabukicho2 = parseSabiasQueItem(sabiasQueKabukicho2Html);
	const sabiasQueKabukicho3 = parseSabiasQueItem(sabiasQueKabukicho3Html);

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#d2ebf0",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
			</Box>

			{/* PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{shinjukuImage && !shinjukuError ? (
					<img
						src={shinjukuImage}
						alt="Shinjuku"
						onError={() => setShinjukuError(true)}
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				) : (
					<ImagenNoDisponibleKawaii
						width="100%"
						height={200}
						fontFamily={fontFamily || "'Poppins', sans-serif"}
					/>
				)}
				{shinjukuSticker && !shinjukuStickerError && (
					<img
						src={shinjukuSticker}
						alt=""
						onError={() => setShinjukuStickerError(true)}
						style={{
							width: 100,
							position: "absolute",
							bottom: 55,
							left: 21,
						}}
					/>
				)}

				{shinjukuTitle && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 32,
							textAlign: "center",
							position: "absolute",
							bottom: 0,
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{shinjukuTitle}
					</Box>
				)}
			</Box>

			<Box sx={{ padding: "10px 12px" }}>
				{introText && (
					<Box data-block-id="text-1">
						<Box
							component="p"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								fontSize: 13,
								color: "#333",
								margin: 0,
								padding: 0,
							}}
						>
							{introText}
						</Box>
					</Box>
				)}

				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						gap: "16px",
						position: "relative",
					}}
				>
					{(miradorTitle || miradorTextShort || miradorTextLong) && (
						<Box
							data-block-id="text-2"
							sx={{
								display: "flex",
								gap: "20px",
								alignItems: "flex-start",
							}}
						>
							<Box>
								{miradorTitle && (
									<Box component="h3" sx={estiloH3}>
										{miradorTitle}
									</Box>
								)}
								{(miradorTextShort || miradorTextLong) && (
									<Box component="p" sx={estiloP}>
										{isSmallScreen ? miradorTextShort : miradorTextLong}
									</Box>
								)}
							</Box>
							{(karaokeImage || karaokeStickerHtml) && (
								<Box sx={{ position: "relative" }}>
									<Box
										sx={{
											backgroundColor: "#d97b2d",
											border: "6px solid #f28b35",
											padding: "16px 0px",
											fontSize: 9,
											borderRadius: "50%",
											marginTop: 0,
											width: 140,
											height: 140,
											color: "white",
											textAlign: "center",
											display: "flex",
											justifyContent: "center",
											flexDirection: "column",
											alignItems: "center",
											fontFamily: `${
												fontFamily || "'Poppins', sans-serif"
											} !important`,
										}}
									>
										{karaokeImage &&
											(!karaokeError ? (
												<img
													src={karaokeImage}
													alt="Karaoke"
													onError={() => setKaraokeError(true)}
													style={{ width: 60 }}
												/>
											) : (
												<ImagenNoDisponibleKawaii
													width={60}
													height={60}
													fontFamily={fontFamily || "'Poppins', sans-serif"}
													compact
												/>
											))}
										{karaokeStickerHtml && (
											<Box
												sx={{ fontSize: 9 }}
												dangerouslySetInnerHTML={{ __html: karaokeStickerHtml }}
											/>
										)}
									</Box>
								</Box>
							)}
						</Box>
					)}

					{(kabukichoTitle || kabukichoText || godzilaImage) && (
						<Box
							data-block-id="text-3"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "16px",
								position: "relative",
								marginTop: isSmallScreen ? 0 : "-32px",
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: "20px",
									alignItems: "flex-start",
								}}
							>
								{godzilaImage &&
									(!godzilaError ? (
										<img
											src={godzilaImage}
											alt="Godzila"
											onError={() => setGodzilaError(true)}
											style={{
												width: "40%",
												height: 110,
												borderRadius: "12px",
												objectFit: "cover",
											}}
										/>
									) : (
										<ImagenNoDisponibleKawaii
											width="40%"
											height={110}
											fontFamily={fontFamily || "'Poppins', sans-serif"}
											compact
										/>
									))}
								<Box sx={{ width: godzilaImage ? "60%" : "100%" }}>
									{kabukichoTitle && (
										<Box component="h3" sx={estiloH3}>
											{kabukichoTitle}
										</Box>
									)}
									{kabukichoText && (
										<Box component="p" sx={estiloP}>
											{kabukichoText}
										</Box>
									)}
								</Box>
								{sabiasQueKabukicho1 && (
									<Box sx={{ position: "absolute", top: "80px", left: "35%" }}>
										<SabiasQue
											icono={sabiasQueKabukicho1.icono}
											texto={sabiasQueKabukicho1.texto}
										/>
									</Box>
								)}
								{sabiasQueKabukicho2 && (
									<Box sx={{ position: "absolute", top: "10px", left: "35%" }}>
										<SabiasQue
											icono={sabiasQueKabukicho2.icono}
											texto={sabiasQueKabukicho2.texto}
										/>
									</Box>
								)}
								{sabiasQueKabukicho3 && (
									<Box
										sx={{ position: "absolute", top: "-35px", right: "30%" }}
									>
										<SabiasQue
											icono={sabiasQueKabukicho3.icono}
											texto={sabiasQueKabukicho3.texto}
										/>
									</Box>
								)}
							</Box>
						</Box>
					)}

					{(omoideTitle || omoideText) && (
						<Box data-block-id="text-4" sx={{ position: "relative" }}>
							{omoideTitle && (
								<Box component="h3" sx={estiloH3}>
									{omoideTitle}
								</Box>
							)}
							{omoideText && (
								<Box component="p" sx={estiloP}>
									{omoideText}
								</Box>
							)}
						</Box>
					)}

					{(comoLlegarTitle || comoLlegarListHtml) && (
						<Box
							data-block-id="text-5"
							sx={{
								backgroundColor: "#ffe3cc",
								border: "3px solid #f28b35",
								borderRadius: "12px",
								padding: "16px 20px",
								fontSize: 12,
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								color: "#333",
								width: isSmallScreen ? "60%" : "100%",
								marginTop: isSmallScreen ? 0 : "-8px",
							}}
						>
							{comoLlegarTitle && (
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 6,
										color: "#f28b35",
										textTransform: "uppercase",
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
									}}
								>
									{comoLlegarTitle}
								</Box>
							)}
							{comoLlegarListHtml && (
								<Box
									sx={{
										paddingLeft: "20px",
										margin: 0,
										lineHeight: 1.5,
										"& ul": { paddingLeft: "20px", margin: 0 },
									}}
									dangerouslySetInnerHTML={{ __html: comoLlegarListHtml }}
								/>
							)}
						</Box>
					)}
					{donQuijoteImage && !donQuijoteError && (
						<img
							src={donQuijoteImage}
							alt=""
							onError={() => setDonQuijoteError(true)}
							style={{
								width: 100,
								position: "absolute",
								bottom: isSmallScreen ? -20 : 0,
								right: isSmallScreen ? -30 : 0,
							}}
						/>
					)}
					{stickerDonQuijoteHtml && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "50px" : "50px",
								right: isSmallScreen ? "10px" : "60px",
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: isSmallScreen ? 150 : 200,
							}}
							dangerouslySetInnerHTML={{ __html: stickerDonQuijoteHtml }}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template59.thumbnail = "/images/template-1.png";

export default Template59;
